import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { routes, type RouteConfig } from "@/app/router/routes";
import { cn } from "@/shared/libs/utils";

interface BreadcrumbItem {
  name: string;
  path?: string;
}

// 재귀적으로 경로와 일치하는 route를 찾는 함수
function findRouteByPath(
  path: string,
  routeList: RouteConfig[] = routes
): { route: RouteConfig; parent?: RouteConfig } | null {
  for (const route of routeList) {
    if (route.path === path) {
      return { route };
    }
    if (route.children) {
      for (const child of route.children) {
        if (child.path === path) {
          return { route: child, parent: route };
        }
      }
    }
  }
  return null;
}

export function Breadcrumbs() {
  const location = useLocation();
  const currentPath = location.pathname;

  // 대시보드 페이지에서는 breadcrumb 표시 안 함
  if (currentPath === "/") return null;

  const breadcrumbs: BreadcrumbItem[] = [];

  // 현재 경로에 해당하는 route 찾기
  const routeInfo = findRouteByPath(currentPath);

  if (routeInfo) {
    // 부모 메뉴가 있으면 추가
    if (routeInfo.parent) {
      breadcrumbs.push({
        name: routeInfo.parent.name,
        path: undefined, // 부모는 그룹이므로 경로 없음
      });
    }

    // 현재 페이지 추가
    breadcrumbs.push({
      name: routeInfo.route.name,
      path: routeInfo.route.path,
    });
  }

  if (breadcrumbs.length === 0) return null;

  return (
    <nav className="mb-4 flex items-center gap-1 text-sm text-muted-foreground">
      <Link to="/" className="flex items-center hover:text-foreground">
        <Home className="h-4 w-4" />
      </Link>
      {breadcrumbs.map((crumb, index) => (
        <div key={`${crumb.name}-${index}`} className="flex items-center gap-1">
          <ChevronRight className="h-4 w-4" />
          {crumb.path ? (
            <Link
              to={crumb.path}
              className={cn(
                "hover:text-foreground",
                index === breadcrumbs.length - 1 && "font-medium text-foreground"
              )}
            >
              {crumb.name}
            </Link>
          ) : (
            <span className="text-muted-foreground">{crumb.name}</span>
          )}
        </div>
      ))}
    </nav>
  );
}

